import { useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowUpRight, CalendarDays, MousePointerClick, Quote } from "lucide-react";
import {
  SERIES_URL,
  BOOK2_COVER,
  BOOK2_COVER_BACK,
  BOOK2_DATE,
  BOOK2_ASIN,
  BOOK2_URL,
  BOOK2_PRICE,
  BOOK2_TAGLINE,
  BOOK2_EARLY_REVIEW,
  BOOK2_ISBN,
  BOOK2_PAGES,
} from "@/constants/site";
import { FadeUp, EASE } from "./Reveal";

const DETAILS = [
  { label: "Series", value: "The Aurora Chronicles — Book 2 of 2" },
  { label: "Published", value: "Sept 15, 2026" },
  { label: "Print length", value: BOOK2_PAGES },
  { label: "Language", value: "English" },
  { label: "Publisher", value: "Independently published" },
  { label: "ISBN-13", value: BOOK2_ISBN },
  { label: "Formats", value: `Paperback ${BOOK2_PRICE}` },
  { label: "ASIN", value: BOOK2_ASIN, href: BOOK2_URL },
];

export const SeriesTeaser = () => {
  const reduced = useReducedMotion();
  const [stage, setStage] = useState(0); // 0 = front cover, 1 = back cover

  const flipFace = () => setStage((s) => (s === 0 ? 1 : 0));

  return (
    <section id="series" data-testid="series-section" className="relative py-28 md:py-36 overflow-hidden">
      <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
        <div
          className="absolute left-[-10%] top-1/3 w-[60%] h-[55%]"
          style={{ background: "radial-gradient(ellipse at center, rgba(56,189,248,0.10), transparent 65%)", filter: "blur(40px)" }}
        />
      </div>

      <div className="relative max-w-[1400px] mx-auto px-6 lg:px-12">
        <div className="grid lg:grid-cols-12 gap-16 lg:gap-20 items-center">
          {/* Flip cover: front <-> back */}
          <motion.div
            className="lg:col-span-5 flex flex-col items-center gap-6 order-1"
            initial={{ opacity: 0, y: 48 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.9, ease: EASE }}
          >
            <div
              className="relative [perspective:1300px] cursor-pointer"
              data-testid="book2-flip-card"
              onClick={flipFace}
              role="button"
              aria-label="Flip the cover"
            >
              <div
                className="absolute -inset-14 pointer-events-none"
                style={{ background: "radial-gradient(ellipse at center, rgba(56,189,248,0.20), rgba(16,185,129,0.08) 50%, transparent 72%)", filter: "blur(30px)" }}
                aria-hidden="true"
              />
              <motion.div
                className="relative preserve-3d w-56 sm:w-72 lg:w-[340px] aspect-[120/190]"
                animate={{ rotateY: (reduced ? 0 : stage) * 180 }}
                transition={{ duration: 1.2, ease: EASE }}
              >
                {/* Front cover */}
                <div className="flip-face absolute inset-0 clip-frame border border-white/10 bg-[#050505] shadow-[0_40px_120px_-20px_rgba(56,189,248,0.40)]">
                  <img
                    src={BOOK2_COVER}
                    alt="Aurora Unbound — The Aurora Chronicles, Book Two, by Michael B. Ford, front cover"
                    className="w-full h-full object-cover"
                    loading="lazy"
                    data-testid="book2-cover-img"
                  />
                  <div className="absolute inset-0 pointer-events-none bg-gradient-to-tr from-transparent via-white/5 to-white/10" />
                </div>
                {/* Back cover */}
                <div className="flip-face flip-back absolute inset-0 clip-frame border border-white/10 bg-[#050505] shadow-[0_40px_120px_-20px_rgba(56,189,248,0.40)]">
                  <img
                    src={BOOK2_COVER_BACK}
                    alt="Aurora Unbound back cover with the full story synopsis"
                    className="w-full h-full object-cover"
                    loading="lazy"
                    data-testid="book2-cover-back-img"
                  />
                  <div className="absolute inset-0 pointer-events-none bg-gradient-to-tl from-transparent via-white/5 to-white/10" />
                </div>
              </motion.div>
            </div>
            <span
              className="font-tech text-[10px] uppercase tracking-[0.3em] text-zinc-500 flex items-center gap-2"
              data-testid="book2-flip-hint"
            >
              <MousePointerClick size={13} className="text-sky-400" />
              Tap the cover to read the back
            </span>
          </motion.div>

          {/* Copy */}
          <div className="lg:col-span-7 order-2">
            <FadeUp>
              <span className="font-tech text-[11px] uppercase tracking-[0.3em] text-sky-400">
                Now available — Book Two of the series
              </span>
              <h2 className="font-display font-light tracking-tighter leading-[1.02] text-[clamp(2.4rem,5vw,5rem)] mt-8">
                Aurora <span className="italic text-sky-300">Unbound</span>
              </h2>
              <p className="font-display italic font-light text-xl md:text-2xl text-sky-300/90 tracking-tight mt-5" data-testid="book2-tagline">
                {BOOK2_TAGLINE}
              </p>
            </FadeUp>

            <FadeUp delay={0.1}>
              <p className="mt-8 max-w-xl text-zinc-300 font-light leading-relaxed text-base md:text-lg">
                The world knows Alex McLeod exists. Now he&rsquo;s about to discover he was
                never alone. Across Canada, others are awakening — and Alex, Jordan, and Ty
                set out to find them before someone else does.
              </p>
            </FadeUp>

            <FadeUp delay={0.13}>
              <figure
                className="mt-8 max-w-xl border-l-2 border-sky-400/60 pl-6 flex items-start gap-4"
                data-testid="book2-early-review"
              >
                <Quote size={18} className="text-sky-400 shrink-0 mt-1.5" />
                <div>
                  <blockquote className="font-display italic font-light text-xl md:text-2xl text-zinc-200 leading-snug">
                    &ldquo;{BOOK2_EARLY_REVIEW}&rdquo;
                  </blockquote>
                  <figcaption className="font-tech text-[10px] uppercase tracking-[0.3em] text-zinc-500 mt-3">
                    Early reader review
                  </figcaption>
                </div>
              </figure>
            </FadeUp>

            <FadeUp delay={0.18}>
              <div
                className="mt-10 inline-flex items-center gap-5 border border-sky-400/25 bg-sky-400/[0.04] px-7 py-5"
                data-testid="book2-available-badge"
              >
                <CalendarDays size={20} className="text-sky-400 shrink-0" />
                <div>
                  <p className="font-tech text-[10px] uppercase tracking-[0.3em] text-zinc-500">
                    Available now · Released
                  </p>
                  <p className="font-display italic font-light text-2xl md:text-3xl text-white mt-1">
                    {BOOK2_DATE}
                  </p>
                </div>
              </div>
            </FadeUp>

            <FadeUp delay={0.2}>
              <div className="mt-12 flex flex-wrap items-center gap-6">
                <a
                  href={BOOK2_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="series-order-button"
                  className="group inline-flex items-center gap-3 bg-sky-500 text-black font-tech text-xs uppercase tracking-[0.25em] px-8 py-4 hover:bg-sky-400 hover:-translate-y-0.5 transition-[background-color,transform] duration-300"
                >
                  Order Aurora Unbound
                  <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </a>
                <a
                  href={SERIES_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="series-amazon-button"
                  className="group inline-flex items-center gap-3 border border-white/15 text-zinc-200 font-tech text-xs uppercase tracking-[0.25em] px-8 py-4 hover:border-sky-400/70 hover:text-sky-300 hover:-translate-y-0.5 transition-[border-color,color,transform] duration-300"
                >
                  View the full series
                  <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </a>
              </div>
              <p className="mt-8 font-tech text-[10px] uppercase tracking-[0.3em] text-zinc-600" data-testid="series-horizon-note">
                On the horizon — Book Three: Aurora Legacy · Book Four: Aurora Convergence
              </p>
            </FadeUp>
          </div>
        </div>

        {/* Full spec grid — just like Book One */}
        <FadeUp delay={0.1} className="mt-24">
          <dl className="grid sm:grid-cols-2 lg:grid-cols-4 gap-x-12">
            {DETAILS.map(({ label, value, href }) => (
              <div
                key={label}
                className="flex flex-col gap-2 border-t border-white/10 py-5"
                data-testid={`book2-detail-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              >
                <dt className="font-tech text-[10px] uppercase tracking-[0.3em] text-zinc-500">
                  {label}
                </dt>
                <dd className="text-zinc-200 font-light text-sm md:text-base">
                  {href ? (
                    <a
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`book2-detail-link-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                      className="hover:text-sky-300 transition-colors duration-300"
                    >
                      {value}
                    </a>
                  ) : (
                    value
                  )}
                </dd>
              </div>
            ))}
          </dl>
        </FadeUp>
      </div>
    </section>
  );
};

export default SeriesTeaser;
